/**
 * Pure helpers for the Repository access page. Kept out of the components so the rules they encode —
 * which mirror the orchestrator's — can be tested on their own.
 */
import type {
  DeploymentGitRouting,
  GitCredential,
  GitCredentialStatus,
  GitRoutingRule,
} from '@/types/gitRouting'
import { GIT_PROVIDER_OPTIONS } from '@/types/gitRouting'

/** The display name for a provider key, or the key itself when it is not one the page knows. */
export function providerLabel(provider: string | null | undefined): string {
  return GIT_PROVIDER_OPTIONS.find((p) => p.value === provider?.toLowerCase())?.label ?? provider ?? ''
}

/**
 * The server's glob: `*` matches anything, including `/`, and the comparison ignores case
 * (`CompositeRepoUrlResolver.UrlGlobMatches`).
 */
export function patternMatches(pattern: string, url: string): boolean {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')
  return new RegExp(`^${escaped}$`, 'i').test(url)
}

/**
 * The earlier rule that matches everything `rule` matches, so `rule` can never be reached; undefined when
 * none does. An earlier pattern that matches this pattern's own text covers every URL this one could match.
 */
export function shadowingRule(
  pattern: string,
  position: number,
  rules: GitRoutingRule[],
  ignoreId?: string,
): GitRoutingRule | undefined {
  return rules
    .filter((r) => r.id !== ignoreId && r.position < position)
    .sort((a, b) => a.position - b.position)
    .find((r) => patternMatches(r.urlPattern, pattern))
}

/** Text to show for a pattern: an example URL it matches, with each `*` filled in. */
export function examplePatternMatch(pattern: string): string {
  return pattern.replace(/\*+$/, 'example-repo').replace(/\*/g, 'example')
}

/** The credential a host default sends: `{provider}:default`, or ADO's per-organisation name. */
export function hostDefaultCredential(provider: string): string {
  return provider === 'ado' ? 'ado:org-{organisation}' : `${provider}:default`
}

/** Whether a credential name is a placeholder rather than a name a token can be stored under. */
export function isCredentialTemplate(name: string): boolean {
  return name.includes('{')
}

/**
 * Set / Missing / Unknown for a credential the page did not get a status for from the server — the
 * deployment's rules and host defaults. Unknown whenever the credential list could not be read.
 */
export function credentialStatus(name: string, credentials: GitCredential[] | undefined): GitCredentialStatus {
  if (credentials === undefined || isCredentialTemplate(name)) {
    return 'Unknown'
  }
  return credentials.some((c) => c.name.toLowerCase() === name.toLowerCase()) ? 'Set' : 'Missing'
}

/** Who refers to a credential: the tenant's rules by position, and whether the deployment defaults do. */
export interface CredentialUsage {
  rules: GitRoutingRule[]
  defaults: boolean
}

export function credentialUsage(
  name: string,
  rules: GitRoutingRule[],
  deployment: DeploymentGitRouting | undefined,
): CredentialUsage {
  const same = (other: string | null | undefined) => other?.toLowerCase() === name.toLowerCase()

  return {
    rules: rules.filter((r) => same(r.credentialName)).sort((a, b) => a.position - b.position),
    defaults:
      (deployment?.rules ?? []).some((r) => same(r.credentialRef ?? hostDefaultCredential(r.provider)))
      || (deployment?.hostDefaults ?? []).some((h) => same(hostDefaultCredential(h.provider))),
  }
}

/** "rule 1, rule 3" / "defaults" / both, for the credentials table's Used by column. */
export function describeUsage(usage: CredentialUsage): string {
  const parts = usage.rules.map((r) => `rule ${r.position}`)
  if (usage.defaults) {
    parts.push('defaults')
  }
  return parts.join(', ')
}

/**
 * Where a rule's URLs go once it is deleted: the next tenant rule that covers its pattern, else the
 * deployment rule that does, else the host default for its host — or nowhere.
 */
export function fallbackAfterDelete(
  rule: GitRoutingRule,
  rules: GitRoutingRule[],
  deployment: DeploymentGitRouting | undefined,
): string | undefined {
  const later = rules
    .filter((r) => r.id !== rule.id && r.position > rule.position)
    .sort((a, b) => a.position - b.position)
    .find((r) => patternMatches(r.urlPattern, rule.urlPattern))
  if (later) {
    return `rule ${later.position} (${later.credentialName})`
  }

  const deploymentRule = (deployment?.rules ?? []).find((r) => patternMatches(r.urlPattern, rule.urlPattern))
  if (deploymentRule) {
    return `the deployment rule for ${deploymentRule.urlPattern} (${deploymentRule.credentialRef ?? hostDefaultCredential(deploymentRule.provider)})`
  }

  const host = hostOf(rule.urlPattern)
  const hostDefault = (deployment?.hostDefaults ?? []).find((h) => h.host.toLowerCase() === host)
  return hostDefault
    ? `the deployment default (${hostDefaultCredential(hostDefault.provider)})`
    : undefined
}

/** The lower-cased host of an https URL or pattern; empty when there is none. */
export function hostOf(urlOrPattern: string): string {
  const match = /^https?:\/\/([^/]+)/i.exec(urlOrPattern)
  return match ? match[1].toLowerCase() : ''
}

/**
 * A rule pattern for the owner a test URL resolved to: `https://{host}/{owner}/*`. Azure DevOps addresses
 * an organisation the same way on dev.azure.com, which is what its credentials are keyed by.
 */
export function ownerPattern(url: string, owner: string | null | undefined): string {
  const host = hostOf(url)
  return host && owner ? `https://${host}/${owner}/*` : ''
}

/** The credential name a new rule for `owner` would suggest: `github:acme`, or `ado:org-contoso`. */
export function suggestedCredentialName(provider: string, owner: string | null | undefined): string {
  const slug = (owner ?? '').toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^[^a-z0-9]+/, '')
  if (!slug) {
    return ''
  }
  return provider === 'ado' ? `ado:org-${slug}` : `${provider}:${slug}`
}

// ── the name grammar the store enforces (Ignix.Credentials.GitCredentialNames) ───────────────────

const CREDENTIAL_NAME = /^[a-z][a-z0-9-]{0,31}:[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/

/** What is wrong with a credential name for `provider`, or undefined; the same checks the server makes. */
export function credentialNameProblem(name: string, provider: string): string | undefined {
  if (!CREDENTIAL_NAME.test(name)) {
    return "Use letters, digits, '.', '_' or '-', starting with a letter or digit."
  }

  const [prefix, rest] = name.split(':')
  if (prefix !== provider) {
    return `A ${providerLabel(provider)} rule needs a ${provider}: credential.`
  }

  if (provider === 'ado' && rest !== 'default' && !(rest.startsWith('org-') && rest.length > 'org-'.length)) {
    return 'An Azure DevOps credential is ado:default or ado:org-{organisation}, e.g. ado:org-contoso.'
  }

  return undefined
}

/** `ids` with `id` moved (or inserted) so it sits at one-based `position`. */
export function placeAt(ids: string[], id: string, position: number): string[] {
  const rest = ids.filter((existing) => existing !== id)
  const index = Math.min(Math.max(position - 1, 0), rest.length)
  return [...rest.slice(0, index), id, ...rest.slice(index)]
}

/** The part of a pattern to start a test URL from: everything before its first `*`. */
export function patternStem(pattern: string): string {
  const star = pattern.indexOf('*')
  return star < 0 ? pattern : pattern.slice(0, star)
}
